import { useState, useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import CloseButton from '../../components/CloseButton';
import ConfirmDialog from '../../components/ConfirmDialog';
import InputTimed from '../../components/InputTimed';
import UserSelector from '../../components/UserSelector';
import useCalendarEvent from '../../hooks/useCalendarEvent';
import useProfileData from '../../hooks/useProfileData';
import { setEventWindowData } from '../../Redux/store';

const STATUS_OPTIONS = ['pending', 'confirmed', 'cancelled'];

// TODO: Known bug — while a new event is being created (creatingNewRef=true),
// additional field commits (title, contact, status, note) are silently dropped
// instead of being queued. Fix: accumulate { field: value } pairs in a ref,
// then after createEvent resolves and loading state goes idle, apply all
// queued updates via updateEvent in a single batch.

/**
 * EventDisplay - Inner content for creating/editing a calendar event.
 * Simple view and edit component that receives data via props only.
 *
 * Props:
 *   eventData: Full event data object { id, title, contact, status, note, date }
 *   onClose: callback when user wants to close
 *   onCreateNew: callback when a new event is successfully created (receives the new event data)
 */
function EventDisplay({ eventData, onClose, onCreateNew }) {
  const { createEvent, updateEvent, deleteEvent } = useCalendarEvent();
  const dispatch = useDispatch();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const creatingNewRef = useRef(false);
  const { profile: contactProfile } = useProfileData(eventData?.contact);

  const { id: eventId, date, title, subtitle, contact, status, note } = eventData || {};
  const isNew = eventId === 'new';

  if (!eventData) return null;

  const handleDelete = () => {
    if (isNew) { onClose(); return; }
    deleteEvent(eventId, date); // optimistic — runs in background
    onClose(); // close immediately, event already removed from Redux
  };

  const handleTitleCommit = async (value) => {
    if (isNew) {
      if (creatingNewRef.current) {
        return;
      }
      creatingNewRef.current = true;
      const realId = await createEvent(date, { title: value });
      creatingNewRef.current = false;
      if (realId && onCreateNew) {
        onCreateNew({ ...eventData, id: realId, title: value });
      }
    } else {
      await updateEvent(eventId, date, { title: value });
    }
  };

  const handleContactSelected = (user) => {
    if (!isNew) {
      // Don't await - let it run in background for optimistic UI
      updateEvent(eventId, date, { contact: user.id });
      // Update Redux state immediately for instant UI feedback
      dispatch(setEventWindowData({
        day: date,
        eventData: { ...eventData, contact: user.id }
      }));
    }
  };

  const handleContactRemoved = () => {
    if (!isNew) {
      // Don't await - let it run in background for optimistic UI
      updateEvent(eventId, date, { contact: null });
      // Update Redux state immediately for instant UI feedback
      dispatch(setEventWindowData({
        day: date,
        eventData: { ...eventData, contact: null }
      }));
    }
  };

  return (
    <>
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto px-5 pt-5 space-y-4">
        {/* Title */}
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Title</p>
          <InputTimed
            defaultValue={title || ''}
            onCommit={handleTitleCommit}
            placeholder="Event title..."
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {/* Subtitle */}
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Subtitle</p>
          <InputTimed
            defaultValue={subtitle || ''}
            onCommit={async (value) => {
              if (!isNew) {
                await updateEvent(eventId, date, { subtitle: value });
              }
            }}
            placeholder="Event subtitle..."
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        {/* Contact */}
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Contact</p>
          <UserSelector
            selectedUserId={contact}
            onUserSelected={handleContactSelected}
            onUserRemoved={handleContactRemoved}
            placeholder="Search @tag or name..."
          />
        </div>

        {/* Status */}
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Status</p>
          <div className="flex gap-2">
            {STATUS_OPTIONS.map(s => (
              <button
                key={s}
                onClick={async () => {
                  if (!isNew) {
                    await updateEvent(eventId, date, { status: s });
                  }
                }}
                className={`flex-1 rounded-full py-1.5 text-xs font-medium capitalize transition-colors ${
                  status === s
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-secondary text-muted-foreground hover:bg-primary/10'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Note */}
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-primary">Note</p>
          <InputTimed
            type="textarea"
            defaultValue={note || ''}
            onCommit={async (value) => {
              if (!isNew) {
                await updateEvent(eventId, date, { note: value });
              }
            }}
            placeholder="Event notes..."
            className="min-h-20 w-full resize-none rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

      </div>

      {/* Fixed bottom buttons */}
      <div className="p-3 flex-shrink-0 flex gap-2">
        {!isNew && (
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="rounded-full bg-destructive/10 px-4 py-2.5 text-sm font-semibold text-destructive hover:bg-destructive/20"
          >
            Delete
          </button>
        )}
        {showDeleteConfirm && (
          <ConfirmDialog
            message={`Delete "${title || 'this event'}"? This cannot be undone.`}
            confirmLabel="Delete"
            destructive
            onConfirm={handleDelete}
            onCancel={() => setShowDeleteConfirm(false)}
          />
        )}
        <button
          onClick={onClose}
          className="flex-1 rounded-full bg-primary py-2.5 text-sm font-semibold text-primary-foreground"
        >
          Done
        </button>
      </div>
    </>
  );
}

export default EventDisplay;
